<template>
  <div class="nearest-page">
  <div class="nearest-wrap">
    <h1>Nearest Distributors</h1>

    <div class="controls">
      <div class="field">
        <label>ZIP</label>
        <input v-model="zip" maxlength="5" placeholder="93720" />
      </div>
      <div class="field">
        <label>Limit</label>
        <input v-model.number="limit" type="number" min="1" max="50" />
      </div>
      <div class="field">
        <label>Max miles (optional)</label>
        <input v-model.number="maxMiles" type="number" min="1" />
      </div>
      <div class="field">
        <label>Source</label>
        <select v-model="source">
          <option value="cache">Cache (fast)</option>
          <option value="live">Live (ERP)</option>
        </select>
      </div>
      <button class="btn" :disabled="loading || !validZip" @click="fetchNearest">
        {{ loading ? 'Loading…' : 'Find Nearest' }}
      </button>
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <div v-if="results.length" class="results">
      <div v-for="(r,i) in results" :key="r.account_id + '-' + i" class="card">
        <div class="left">
          <div class="name" :title="trim(r.name)">{{ trim(r.name) }}</div>
          <div class="sub">
            <span v-if="r.city">{{ r.city }}</span><span v-if="r.city && r.state">, </span><span v-if="r.state">{{ r.state }}</span>
            <span v-if="r.zip"> • {{ r.zip }}</span>
          </div>
          <div class="meta">Acct: {{ trim(r.account_id) }} • Customer Code: {{ trim(r.customer_code) }}</div>
        </div>
        <div class="right">
          <div class="dist">{{ Number(r.distance_miles).toFixed(1) }} mi</div>
          <a class="maps" :href="mapsLink(r)" target="_blank" rel="noopener">Open in Maps</a>
        </div>
      </div>
    </div>

    <div v-else-if="!loading && !error" class="empty">
      Enter a ZIP and click “Find Nearest”.
    </div>
  </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'NearestDistributors',
  data() {
    return {
      zip: '93720',
      limit: 10,
      maxMiles: '',
      source: 'cache', // or 'live'
      results: [],
      loading: false,
      error: '',
      // Use env if provided; otherwise match your existing working calls via pablogic.com
      apiBase: process.env.VUE_APP_API_BASE || ''
    };
  },
  computed: {
    validZip() { return /^\d{5}$/.test((this.zip || '').toString()); }
  },
  methods: {
    trim(v) { return (v && typeof v === 'string') ? v.trim() : v; },
    mapsLink(r) {
      const q = encodeURIComponent(`${this.trim(r.name) || ''} ${r.city || ''} ${r.state || ''} ${r.zip || ''}`);
      return `https://www.google.com/maps/search/?api=1&query=${q}`;
    },
    async fetchNearest() {
      this.error = '';
      this.results = [];
      if (!this.validZip) { this.error = 'Please enter a 5-digit ZIP.'; return; }
      this.loading = true;
      try {
        const params = {
          zip: this.zip.toString().slice(0,5),
          limit: this.limit || 10,
          source: this.source
        };
        if (this.maxMiles) params.maxMiles = this.maxMiles;

        const url = `${this.apiBase}/nearestDistributors`;
        const { data, headers } = await axios.get(url, { params });

        // Guard in case a proxy misroutes and returns HTML
        const ct = String(headers['content-type'] || '');
        if (ct.includes('text/html')) throw new Error('Backend returned HTML (check API base URL).');

        this.results = Array.isArray(data) ? data.map(r => ({
          ...r,
          account_id: this.trim(r.account_id),
          name: this.trim(r.name),
          customer_code: this.trim(r.customer_code)
        })) : [];
      } catch (e) {
        this.error = String(e.message || e);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>

.nearest-page {
  min-height: 100vh;
  width: 100%;
  background-color: #0b0101; /* Pablo */
  display: flex;
  justify-content: center;
}

.nearest-wrap {
  width: 100%;
  max-width: 900px;
  padding: 24px 16px 30px 16px;
  font-family: Garamond, serif;
  color: #cbbfbf;
}


/* Heading */
h1 {
  text-align: center;
  margin: 70px 0 16px 0; /* leave space for App.vue top controls */
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 40px;
  letter-spacing: 1px;
  color: #cbbfbf;
}

/* Controls panel = machine-row / Pablo panel */
.controls {
  display: grid;
  grid-template-columns: repeat(5, minmax(120px, 1fr));
  gap: 12px;
  align-items: end;

  background-color: #708090a4; /* machine-row */
  border: 1px solid rgba(203, 191, 191, 0.22);
  padding: 14px;
  border-radius: 14px;
  box-shadow: 0 6px 14px rgba(0,0,0,.28);
  margin-bottom: 16px;
}

/* Labels */
.field label {
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 6px;
  color: #f9fafb;
  opacity: 0.9;
}

/* Inputs = dark Pablo inputs like your modals/selects */
.field input,
.field select {
  width: 100%;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(203, 191, 191, 0.25);
  background-color: #111827; /* dark input */
  color: #e5e7eb;
  outline: none;
}

.field input::placeholder {
  color: rgba(229, 231, 235, 0.6);
}

.field input:focus,
.field select:focus {
  border-color: rgba(147, 197, 253, 0.85);
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.25);
}

/* Button = Pablo action button */
.btn {
  height: 38px;
  background: #5595b7c2; /* Pablo blue */
  color: #0b0101;
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 10px;
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.12s ease, filter 0.12s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Messages */
.error {
  color: #fca5a5;
  margin: 10px 0;
  font-weight: 700;
}

.empty {
  color: rgba(203, 191, 191, 0.85);
  font-size: 14px;
  margin-top: 10px;
}

/* Results list */
.results {
  display: grid;
  gap: 10px;
  max-height: 70vh;
  overflow: auto;
  padding-right: 6px;
}

/* Scrollbar polish (optional, but matches dark theme) */
.results::-webkit-scrollbar {
  width: 10px;
}
.results::-webkit-scrollbar-thumb {
  background: rgba(203, 191, 191, 0.25);
  border-radius: 10px;
}
.results::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.06);
}

/* Result cards = light “unscheduled/work-order” cards */
.card {
  display: flex;
  justify-content: space-between;
  gap: 12px;

  background: #f5f8f79a; /* your existing card */
  border-radius: 15px;
  padding: 14px;

  box-shadow: 0 5px 12px rgba(0,0,0,.22);
  border: 1px solid rgba(0,0,0,0.12);
}

/* Left content */
.left {
  min-width: 0;
  color: #0b0101;
}

.name {
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sub {
  font-size: 14px;
  color: rgba(11, 1, 1, 0.75);
  margin-top: 2px;
}

.meta {
  font-size: 12px;
  color: rgba(11, 1, 1, 0.65);
  margin-top: 6px;
}

/* Right content */
.right {
  text-align: right;
  min-width: 90px;
  color: #0b0101;
}

.dist {
  font-weight: 900;
  font-size: 18px;
}

/* Map link = Pablo blue */
.maps {
  font-size: 12px;
  color: #0b4d6b; /* deepened Pablo blue for contrast on light card */
  text-decoration: none;
  font-weight: 800;
}

.maps:hover {
  text-decoration: underline;
}
</style>
